import React, { useState, useEffect } from 'react';
import { Globe, CheckCircle, AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';
import ComponentIdBadge from '../../../components/ui/ComponentIdBadge';

export default function DomainHealthPanel({ onHealthUpdate, triggerCheck }) {
  const [expanded, setExpanded] = useState(false);
  const [checking, setChecking] = useState(false);
  const [domainHealth, setDomainHealth] = useState({
    status: 'checking',
    checks: {
      dnsResolution: { status: 'pending', message: '' },
      sslCertificate: { status: 'pending', message: '' },
      domainRouting: { status: 'pending', message: '' }
    },
    issues: [],
    details: {
      currentDomain: window?.location?.hostname,
      protocol: window?.location?.protocol,
      port: window?.location?.port || 'default'
    }
  });

  useEffect(() => {
    if (triggerCheck) {
      performDomainCheck();
    }
  }, [triggerCheck]);

  const performDomainCheck = async () => {
    setChecking(true);
    const issues = [];
    const checks = { ...domainHealth?.checks };

    try {
      // DNS Resolution Check
      const currentDomain = window?.location?.hostname;
      if (currentDomain === 'localhost' || currentDomain?.includes('127.0.0.1')) {
        checks.dnsResolution = {
          status: 'warning',
          message: 'Running on localhost - DNS checks skipped in development'
        };
        issues?.push('Development environment detected');
      } else {
        checks.dnsResolution = {
          status: 'healthy',
          message: `Domain ${currentDomain} resolves correctly`
        };
      }

      // SSL Certificate Check
      const protocol = window?.location?.protocol;
      if (protocol === 'https:') {
        checks.sslCertificate = {
          status: 'healthy',
          message: 'SSL certificate is active and valid'
        };
      } else if (currentDomain === 'localhost') {
        checks.sslCertificate = {
          status: 'warning',
          message: 'SSL not required for localhost development'
        };
      } else {
        checks.sslCertificate = {
          status: 'error',
          message: 'SSL certificate not detected - insecure connection'
        };
        issues?.push('Missing SSL certificate');
      }

      // Domain Routing Check
      const isProduction = !currentDomain?.includes('localhost') && !currentDomain?.includes('127.0.0.1');
      if (isProduction) {
        checks.domainRouting = {
          status: 'healthy',
          message: 'Domain routing configured correctly'
        };
      } else {
        checks.domainRouting = {
          status: 'warning',
          message: 'Development environment - production routing not applicable'
        };
      }

      // Determine overall status
      const hasErrors = Object.values(checks)?.some(c => c?.status === 'error');
      const hasWarnings = Object.values(checks)?.some(c => c?.status === 'warning');
      const overallStatus = hasErrors ? 'error' : hasWarnings ? 'warning' : 'healthy';

      const updatedHealth = {
        status: overallStatus,
        checks,
        issues,
        details: {
          currentDomain,
          protocol,
          port: window?.location?.port || 'default',
          environment: isProduction ? 'production' : 'development'
        }
      };

      setDomainHealth(updatedHealth);
      onHealthUpdate?.(updatedHealth);
    } catch (error) {
      console.error('Domain health check error:', error);
      const errorHealth = {
        status: 'error',
        checks: {
          dnsResolution: { status: 'error', message: 'Check failed' },
          sslCertificate: { status: 'error', message: 'Check failed' },
          domainRouting: { status: 'error', message: 'Check failed' }
        },
        issues: ['Health check failed'],
        details: domainHealth?.details
      };
      setDomainHealth(errorHealth);
      onHealthUpdate?.(errorHealth);
    } finally {
      setChecking(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy': return 'text-green-600';
      case 'warning': return 'text-yellow-600';
      case 'error': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'healthy': return <CheckCircle className="w-4 h-4" />;
      case 'warning': return <AlertTriangle className="w-4 h-4" />;
      case 'error': return <AlertTriangle className="w-4 h-4" />;
      default: return <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />;
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 relative">
      <ComponentIdBadge
        id="cmp-domain-health-panel"
        name="Domain Health Panel"
        description="DNS, SSL, and domain routing diagnostics"
      />
      
      {/* Panel Header */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-50 rounded-lg">
              <Globe className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Domain Configuration</h3>
              <p className="text-sm text-gray-600">DNS settings, SSL certificate, and routing verification</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(domainHealth?.status)} bg-opacity-10`}>
              {getStatusIcon(domainHealth?.status)}
              {domainHealth?.status === 'healthy' && 'Healthy'}
              {domainHealth?.status === 'warning' && 'Warning'}
              {domainHealth?.status === 'error' && 'Error'}
              {domainHealth?.status === 'checking' && 'Checking...'}
            </span>
            <button
              onClick={() => setExpanded(!expanded)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              {expanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Panel Content */}
      {expanded && (
        <div className="p-6 space-y-4">
          {/* Domain Details */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-gray-900 mb-3">Current Configuration</h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-600">Domain:</span>
                <span className="ml-2 font-mono text-gray-900">{domainHealth?.details?.currentDomain}</span>
              </div>
              <div>
                <span className="text-gray-600">Protocol:</span>
                <span className="ml-2 font-mono text-gray-900">{domainHealth?.details?.protocol}</span>
              </div>
              <div>
                <span className="text-gray-600">Port:</span>
                <span className="ml-2 font-mono text-gray-900">{domainHealth?.details?.port}</span>
              </div>
              <div>
                <span className="text-gray-600">Environment:</span>
                <span className="ml-2 font-mono text-gray-900">{domainHealth?.details?.environment}</span>
              </div>
            </div>
          </div>

          {/* Health Checks */}
          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-gray-900">Health Checks</h4>
            
            {Object.entries(domainHealth?.checks)?.map(([key, check]) => (
              <div key={key} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                <div className={getStatusColor(check?.status)}>
                  {getStatusIcon(check?.status)}
                </div>
                <div className="flex-1">
                  <div className="text-sm font-medium text-gray-900 capitalize">
                    {key?.replace(/([A-Z])/g, ' $1')?.trim()}
                  </div>
                  <div className="text-xs text-gray-600 mt-1">{check?.message}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Issues */}
          {domainHealth?.issues?.length > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-yellow-900 mb-2">Issues Detected</h4>
              <ul className="space-y-1">
                {domainHealth?.issues?.map((issue, idx) => (
                  <li key={idx} className="text-sm text-yellow-800 flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    {issue}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Recommendations */}
          {domainHealth?.status !== 'healthy' && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-blue-900 mb-2">Recommendations</h4>
              <ul className="space-y-1 text-sm text-blue-800">
                {domainHealth?.checks?.sslCertificate?.status === 'error' && (
                  <li>• Configure SSL certificate for secure HTTPS connections</li>
                )}
                {domainHealth?.details?.environment === 'development' && (
                  <li>• Deploy to production environment for full domain validation</li>
                )}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}